import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../common/Card.jsx';
import { toast } from 'react-toastify';
import localService from '../../services/localService';
import clientService from '../../services/clientService';
import AddLocalModal from './Locaux/AddLocalModal';
import EditLocalModal from './Locaux/EditLocalModal';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import Pagination from '../common/Pagination.jsx';
import { useTheme } from '../../contexts/ThemeContext'; // Add theme context
import ClientSelectDropdown from '../common/ClientSelectDropdown'; // Import ClientSelectDropdown

const Locaux = () => {
  const { isDarkMode } = useTheme(); // Use theme
  const { user } = useAuth();
  const [locaux, setLocaux] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerms: '',
    clientId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState(null);

  // Mobile menu state
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Load locaux from API with filters
  const loadLocaux = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
    setLoading(true);
    setError('');
    
    try {
      // Prepare filter parameters for API
      const apiFilters = {};
      if (filterParams.searchTerms?.trim()) apiFilters.searchTerms = filterParams.searchTerms.trim();
      if (filterParams.clientId) apiFilters.clientId = filterParams.clientId;

      const result = await localService.getLocaux(pageSize, page, apiFilters);
      
      if (result.success && result.data) {
        setLocaux(result.data.list || []);
        setPagination({
          page: result.data.page || page,
          pageSize: result.data.pageSize || pageSize,
          total: result.data.total || 0,
          maxPage: result.data.maxPage || 1
        });
      } else {
        setError(result.message || 'Erreur lors du chargement des locaux');
        setLocaux([]);
      }
    } catch (error) {
      console.error('Error fetching locaux:', error);
      setError('Erreur lors du chargement des locaux');
      setLocaux([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const result = await clientService.getClients(100, 1);
      if (result.success && result.data?.list) {
        setClients(result.data.list);
      }
    } catch (error) {
      console.error('Error fetching clients for filter:', error);
    }
  };

  // Export functionality - using existing table data only
  const handleExport = (format = 'csv') => {
    if (!locaux || locaux.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }

    setExporting(true);
    
    try {
      if (format === 'csv') {
        exportToCSV(locaux);
      } else if (format === 'excel') {
        exportToExcel(locaux);
      } else if (format === 'json') {
        exportToJSON(locaux);
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Erreur lors de l\'exportation des données');
    } finally {
      setExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = (data) => {
    const headers = [
      'ID',
      'Adresse',
      'Client',
      'Responsable',
      'Téléphone',
      'Statut'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(local => [
        local.id || '',
        `"${(local.adresse || '').replace(/"/g, '""')}"`,
        `"${(local.client?.raisonSocial || '').replace(/"/g, '""')}"`,
        `"${(local.responsable || '').replace(/"/g, '""')}"`,
        `"${(local.telResponsable || '').replace(/"/g, '""')}"`,
        local.isDefault ? 'Principal' : 'Secondaire'
      ].join(','))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `locaux-export-${timestamp}.csv`, 'text/csv;charset=utf-8;');
  };

  // Export to JSON
  const exportToJSON = (data) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: data.length,
      pagination: {
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.maxPage,
        totalRecords: pagination.total
      },
      filters: filtersApplied ? filters : null,
      data: data
    };
    
    const jsonContent = JSON.stringify(exportData, null, 2);
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(jsonContent, `locaux-export-${timestamp}.json`, 'application/json');
  };

  // Export to Excel
  const exportToExcel = (data) => {
    const headers = [
      'ID',
      'Adresse',
      'Client',
      'Responsable',
      'Téléphone',
      'Statut'
    ];

    // Excel-compatible CSV with BOM for proper encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join('\t'),
      ...data.map(local => [
        local.id || '',
        (local.adresse || '').replace(/\t/g, ' '),
        (local.client?.raisonSocial || '').replace(/\t/g, ' '),
        (local.responsable || '').replace(/\t/g, ' '),
        (local.telResponsable || '').replace(/\t/g, ' '),
        local.isDefault ? 'Principal' : 'Secondaire'
      ].join('\t'))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `locaux-export-${timestamp}.xlsx`, 'application/vnd.ms-excel');
  };

  // Download file utility
  const downloadFile = (content, filename, mimeType) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTimeout(() => {
        console.log(`Export réussi: ${filename}`);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      setError('Erreur lors du téléchargement du fichier');
    }
  };

  // Load locaux on component mount
  useEffect(() => {
    loadLocaux();
    fetchClients();
  }, []);

  // Handle page change
  const handlePageChange = (newPage) => {
    loadLocaux(pagination.pageSize, newPage, filters);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    loadLocaux(newPageSize, 1, filters);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setFiltersApplied(Object.values(filters).some(value => value.toString().trim() !== ''));
    loadLocaux(pagination.pageSize, 1, filters);
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      searchTerms: '',
      clientId: ''
    };
    setFilters(clearedFilters);
    setFiltersApplied(false);
    loadLocaux(pagination.pageSize, 1, clearedFilters);
    setShowFilters(false);
  };

  const handleAddSuccess = () => {
    loadLocaux();
    toast.success('Local ajouté avec succès');
  };

  const handleEditSuccess = () => {
    loadLocaux();
    toast.success('Local modifié avec succès');
  };

  const handleEdit = (local) => {
    setSelectedLocal(local);
    setShowEditModal(true);
  };

  const handleDelete = (local) => {
    setSelectedLocal(local);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const result = await localService.deleteLocal(selectedLocal.id);
      if (result.success) {
        toast.success('Local supprimé avec succès');
        loadLocaux();
      } else {
        toast.error(result.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression du local');
    } finally {
      setShowDeleteModal(false);
      setSelectedLocal(null);
    }
  };

  const handleRefresh = () => {
    loadLocaux(pagination.pageSize, pagination.page, filters);
  };

  // Render pagination using shared Pagination component
  const renderPagination = () => {
    return (
      <div className={`w-full px-4 py-3 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-6xl mx-auto">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.maxPage || 1}
            pageSize={pagination.pageSize}
            totalItems={pagination.total}
            onPageChange={(p) => handlePageChange(p)}
            onPageSizeChange={(s) => handlePageSizeChange(s)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={`text-2xl sm:text-3xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Locaux</h1>
          <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Gestion des locaux et sites clients
            {filtersApplied && (
              <span className="text-primary-600 font-medium"> (Filtré)</span>
            )}
          </p>
        </div>
        
        {/* Desktop Actions */}
        <div className="hidden lg:flex space-x-3 flex-shrink-0">
          <button 
            onClick={handleRefresh}
            disabled={loading}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              filtersApplied 
                ? 'bg-primary-50 border-primary-300 text-primary-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          

          
          {user?.isAdmin && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium"
            >
              <i className="fas fa-plus mr-2"></i>
              Nouveau Local
            </button>
          )}
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden">
          <div className="flex items-center justify-end space-x-2">
            {user?.isAdmin && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium flex-1 sm:flex-none"
              >
                <i className="fas fa-plus mr-1 sm:mr-2"></i>
                <span className="hidden xs:inline">Nouveau </span>Local
              </button>
            )}
            
            <div className="relative">
              <button 
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="bg-white border border-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
              
              {showMobileActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        handleRefresh();
                        setShowMobileActions(false);
                      }}
                      disabled={loading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className={`fas fa-sync-alt mr-2 w-4 ${loading ? 'animate-spin' : ''}`}></i>
                      Actualiser
                    </button>
                    <button 
                      onClick={() => {
                        setShowFilters(!showFilters);
                        setShowMobileActions(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        filtersApplied 
                          ? 'text-primary-700 bg-primary-50' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <i className="fas fa-filter mr-2 w-4"></i>
                      Filtrer
                      {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
                    </button>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    <div className="px-4 py-1 text-xs text-gray-500">
                      Exporter {locaux.length} local{locaux.length > 1 ? 'x' : ''}
                    </div>
                    <button 
                      onClick={() => {
                        handleExport('csv');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || locaux.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-csv mr-2 w-4 text-green-600"></i>
                      {exporting ? 'Export...' : 'CSV'}
                    </button>
                    <button 
                      onClick={() => {
                        handleExport('excel');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || locaux.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-excel mr-2 w-4 text-green-700"></i>
                      {exporting ? 'Export...' : 'Excel'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`${isDarkMode ? 'bg-gray-900 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'} px-4 py-3 sm:px-6 sm:py-4 rounded-lg`}>
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Export Progress */}
      {exporting && (
        <div className={`${isDarkMode ? 'bg-gray-900 border border-blue-700 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'} px-4 py-3 sm:px-6 sm:py-4 rounded-lg`}>
          <div className="flex items-center">
            <i className="fas fa-download mr-2 animate-pulse"></i>
            <span className="text-sm sm:text-base">Préparation du fichier en cours...</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className={`${isDarkMode ? 'border-l-4 border-l-primary-700 bg-gray-800' : 'border-l-4 border-l-primary-500'}`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className={`${isDarkMode ? 'p-2 bg-gray-700' : 'p-2 bg-primary-100'} rounded-lg mr-3`}>
                  <i className={`fas fa-filter ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filtres de recherche</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Affinez votre recherche de locaux</p>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className={`self-end sm:self-auto ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} p-1`}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche par adresse ou responsable
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={(e) => handleFilterChange('searchTerms', e.target.value)}
                  placeholder="Adresse, responsable..."
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-base ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>

              {user?.isAdmin && (
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-user mr-2 text-gray-400"></i>
                    Client
                  </label>
                  <ClientSelectDropdown
                    value={filters.clientId}
                    onChange={val => handleFilterChange('clientId', val)}
                    options={clients}
                    loading={false}
                    disabled={clients.length === 0}
                    isDarkMode={isDarkMode}
                    placeholder="Tous les clients"
                    required={false}
                  />
                </div>
              )}
            </div>

            {/* Filter Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1 ${isDarkMode ? 'text-gray-200 bg-gray-800 hover:bg-gray-700' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2 ${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>

            {/* Active Filters Summary */}
            {filtersApplied && (
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Filtres actifs:</span>
                  {filters.searchTerms && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'}`}>
                      <i className="fas fa-search mr-1"></i>
                      {filters.searchTerms}
                      <button
                        onClick={() => handleFilterChange('searchTerms', '')}
                        className={`${isDarkMode ? 'ml-1 text-primary-400 hover:text-primary-200' : 'ml-1 text-primary-600 hover:text-primary-800'}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.clientId && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'}`}>
                      <i className="fas fa-user mr-1"></i>
                      Client: {clients.find(c => c.id.toString() === filters.clientId)?.raisonSocial || 'Client sélectionné'}
                      <button
                        onClick={() => handleFilterChange('clientId', '')}
                        className={`${isDarkMode ? 'ml-1 text-primary-400 hover:text-primary-200' : 'ml-1 text-primary-600 hover:text-primary-800'}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Locaux List */}
      <Card className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
            <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Chargement des locaux...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className={`${isDarkMode ? 'text-red-400' : 'text-red-500'} mb-4`}>
              <i className="fas fa-exclamation-triangle text-4xl"></i>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Erreur de chargement</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>{error}</p>
            <button
              onClick={handleRefresh}
              className={`${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'} px-4 py-2 rounded-lg transition-colors`}
            >
              <i className="fas fa-redo mr-2"></i>
              Réessayer
            </button>
          </div>
        ) : locaux.length === 0 ? (
          <div className="text-center py-12">
            <div className={`${isDarkMode ? 'text-gray-600' : 'text-gray-400'} mb-4`}>
              <i className="fas fa-building text-6xl"></i>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Aucun local trouvé</h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {filtersApplied
                ? 'Aucun local ne correspond à vos critères de recherche.'
                : 'Commencez par ajouter votre premier local.'
              }
            </p>
            {filtersApplied ? (
              <button
                onClick={clearFilters}
                className={`${isDarkMode ? 'text-primary-400 hover:text-primary-200' : 'text-primary-600 hover:text-primary-700'} font-medium`}
              >
                <i className="fas fa-times mr-2"></i>
                Effacer les filtres
              </button>
            ) : (
              <button
                onClick={() => setShowAddModal(true)}
                className={`${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'} px-4 py-2 rounded-lg transition-colors`}
              >
                <i className="fas fa-plus mr-2"></i>
                Ajouter un local
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Locaux Table */}
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Local</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Client</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Responsable</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Contact</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Statut</th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-200' : 'text-gray-500'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                  {locaux.map((local) => (
                    <tr key={local.id} className={isDarkMode ? 'hover:bg-gray-700 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className={`${isDarkMode ? 'h-10 w-10 rounded-lg bg-gray-700 flex items-center justify-center' : 'h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center'}`}>
                              <i className={`fas fa-building ${isDarkMode ? 'text-gray-200' : 'text-blue-600'}`}></i>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {local.adresse}
                            </div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ID: {local.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {local.client?.raisonSocial || `${local.client?.nom || ''} ${local.client?.prenom || ''}`.trim() || 'N/A'}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {local.client?.matriculeFiscal || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {local.responsable || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {local.telResponsable ? (
                            <a 
                              href={`tel:${local.telResponsable}`}
                              className={isDarkMode ? 'text-primary-400 hover:text-primary-200' : 'text-primary-600 hover:text-primary-900'}
                            >
                              <i className="fas fa-phone mr-1"></i>
                              {local.telResponsable}
                            </a>
                          ) : (
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          local.isDefault
                            ? (isDarkMode ? 'bg-gray-700 text-green-300 border border-gray-600' : 'bg-green-100 text-green-800')
                            : (isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800')
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            local.isDefault ? (isDarkMode ? 'bg-green-400' : 'bg-green-400') : (isDarkMode ? 'bg-gray-400' : 'bg-gray-400')
                          }`}></span>
                          {local.isDefault ? 'Principal' : 'Secondaire'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {user?.isAdmin && (
                            <>
                              <button
                                onClick={() => handleEdit(local)}
                                className={`${isDarkMode ? 'text-primary-400 hover:text-white hover:bg-gray-700' : 'text-primary-600 hover:text-primary-900 hover:bg-primary-50'} p-2 rounded-lg transition-colors`}
                                title="Modifier"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(local)}
                                className={`${isDarkMode ? 'text-red-400 hover:text-white hover:bg-gray-700' : 'text-red-600 hover:text-red-900 hover:bg-red-50'} p-2 rounded-lg transition-colors`}
                                title="Supprimer"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.maxPage > 1 && renderPagination()}
          </>
        )}
      </Card>

      {/* Modals */}
      {/* Modals: Only show Add/Edit/Delete for admin */}
      {user?.isAdmin && showAddModal && (
        <AddLocalModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {user?.isAdmin && showEditModal && selectedLocal && (
        <EditLocalModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLocal(null);
          }}
          onSuccess={handleEditSuccess}
          local={selectedLocal}
        />
      )}

      {user?.isAdmin && showDeleteModal && selectedLocal && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedLocal(null);
          }}
          onConfirm={confirmDelete}
          title="Supprimer le local"
          message={`Êtes-vous sûr de vouloir supprimer le local "${selectedLocal.adresse}" ? Cette action est irréversible.`}
          itemName={selectedLocal.adresse}
        />
      )}

      {/* Click outside to close mobile actions */}
      {showMobileActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMobileActions(false)}
        />
      )}
    </div>
  );
};

export default Locaux;